import { FC } from 'react';
import { TaskCenterTypes } from '@frontend/api-task-center';
import { IconName } from '@frontend/icons';
import { PopoverMenu, usePopoverMenu } from '@frontend/design-system';
import { StatusTaskOption, PriorityTaskOption } from '../../../types';
import { getSwitcherOptions, getPriorityIcon } from '../../../utils';
import { TaskFormRow, PopoverMenuItems } from '../../common';

type TaskOption = StatusTaskOption | PriorityTaskOption;

type SwitcherProps = {
  label: string;
  icon: IconName;
  buttonIconName: IconName;
  placeholder: string;
  isEditing?: boolean;
  statusValue?: TaskCenterTypes.TaskStatus;
  isRemoveable?: boolean;
  onClickRemove?: () => void;
  trackingIds?: {
    removeButton?: string;
    chipButton?: string;
  };
};

type StatusSwitcherProps = SwitcherProps & {
  onChange: (value: TaskCenterTypes.TaskStatus | '') => void;
  value: TaskCenterTypes.TaskStatus;
  type: 'status';
};

type PrioritySwitcherProps = SwitcherProps & {
  onChange: (value: TaskCenterTypes.PriorityType | '') => void;
  value: TaskCenterTypes.PriorityType;
  type: 'priority';
};

export const Switcher: FC<StatusSwitcherProps | PrioritySwitcherProps> = ({
  type = 'status',
  isEditing = true,
  isRemoveable = true,
  statusValue,
  label,
  icon,
  buttonIconName,
  placeholder,
  onChange,
  value,
  trackingIds,
  onClickRemove,
}) => {
  const popoverOptions = getSwitcherOptions(type, isEditing, statusValue);
  const buttonIcon = type === 'priority' ? getPriorityIcon(value as TaskCenterTypes.PriorityType).name : buttonIconName;

  const selectedOption = popoverOptions.find((option) => option.value === value);

  const { close, getMenuProps, getTriggerProps, getItemProps } = usePopoverMenu({
    placement: 'bottom-start',
  });

  const handleOptionClick = (option: TaskOption) => {
    // Since this is a common type hence inferring types here. Will update this once I find a better way
    if (type === 'status') {
      (onChange as (value: TaskCenterTypes.TaskStatus | '') => void)(option.value as TaskCenterTypes.TaskStatus | '');
    } else if (type === 'priority') {
      (onChange as (value: TaskCenterTypes.PriorityType | '') => void)(
        option.value as TaskCenterTypes.PriorityType | ''
      );
    }
    close();
  };

  return (
    <TaskFormRow
      label={label}
      icon={icon}
      placeholder={placeholder}
      buttonIconName={buttonIcon}
      value={selectedOption?.label ?? ''}
      getTriggerProps={getTriggerProps}
      trackingIds={trackingIds}
      isRemoveable={isRemoveable}
      onClickRemove={onClickRemove}
      variant={selectedOption?.variant}
      isEditing={isEditing}
    >
      <PopoverMenu {...getMenuProps()}>
        <PopoverMenuItems
          data={popoverOptions}
          keyGen={(item: StatusTaskOption | PriorityTaskOption) => `task-switcher-${item.label}`}
          getItemProps={getItemProps}
          onClickItem={handleOptionClick}
          canRender={(option: StatusTaskOption | PriorityTaskOption) => !option.hide}
          renderItem={(option: StatusTaskOption | PriorityTaskOption) => (
            <>
              <option.Icon />
              {option.label}
            </>
          )}
        />
      </PopoverMenu>
    </TaskFormRow>
  );
};
